import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HeroSectionForm.css';

const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
const ALLOWED_TYPES = ['video/mp4', 'video/webm'];

export default function HeroSectionForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [sectionId, setSectionId] = useState(40);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  // Form values (auto-filled from content_published)
  const [formData, setFormData] = useState({
    tagline_prefix: '',
    tagline_accent: '',
    philosophy: '',
    video_file: ''
  });

  // Pending approvals per field
  const [pendingFields, setPendingFields] = useState({
    tagline_prefix: false,
    tagline_accent: false,
    philosophy: false,
    video_file: false
  });

  // Track original values to detect changes
  const [originalData, setOriginalData] = useState({...formData});

  useEffect(() => {
    loadHeroSection();
  }, [apiBase]);

  const loadHeroSection = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${apiBase}/cms/section/40/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        const { section, pending_changes } = result.data;
        
        if (section && section.content_published) {
          let published = section.content_published;
          
          if (typeof published === 'string') {
            try {
              published = JSON.parse(published);
            } catch (e) {
              published = {};
            }
          }

          const publishedEn = published.en || {};

          // Auto-fill form with published data
          const initialData = {
            tagline_prefix: publishedEn.tagline_prefix || '',
            tagline_accent: publishedEn.tagline_accent || '',
            philosophy: publishedEn.philosophy || '',
            video_file: publishedEn.video_file || ''
          };

          setFormData(initialData);
          setOriginalData(initialData);
          setSectionId(section.id);

          // Mark pending fields
          if (Array.isArray(pending_changes)) {
            const pendingMap = {
              tagline_prefix: false,
              tagline_accent: false,
              philosophy: false,
              video_file: false
            };

            pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                pendingMap[change.field_id] = true;
              }
            });

            setPendingFields(pendingMap);
          }
        }
      }
    } catch (err) {
      toast.error('Error loading hero section: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation
    if (file.size > MAX_VIDEO_SIZE) {
      toast.error(`Video max 100MB, your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only MP4 or WebM allowed');
      return;
    }

    setVideoUploading(true);
    setVideoProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setVideoProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          if (response.success) {
            handleFieldChange('video_file', response.filename);
            toast.success('Video uploaded');
          } else {
            toast.error(response.error || 'Upload failed');
          }
        } else {
          toast.error('Upload failed');
        }
        setVideoUploading(false);
        setVideoProgress(0);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload error');
        setVideoUploading(false);
        setVideoProgress(0);
      });

      xhr.open('POST', `${apiBase}/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formData);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setVideoUploading(false);
      setVideoProgress(0);
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    // Check if value changed
    if (formData[fieldName] === originalData[fieldName]) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.replace(/_/g, ' '),
            old_value: originalData[fieldName],
            new_value: formData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Submitted for approval`);
        
        // Mark as pending locally
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        // Update original data
        setOriginalData(prev => ({
          ...prev,
          [fieldName]: formData[fieldName]
        }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  if (loading) {
    return (
      <div className="hero-form-wrapper">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading hero section...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-form-wrapper">
      <ToastContainer />

      <div className="hero-form-header">
        <h2 className="hero-form-title">Hero Section</h2>
        <p className="hero-form-description">Edit and submit each field for approval</p>
      </div>

      {/* Tagline Prefix */}
      <div className="form-section">
        <div className="field-group">
          <label className="field-label">Tagline Prefix</label>
          {pendingFields.tagline_prefix && <span className="pending-badge">Pending</span>}
        </div>
        <input
          type="text"
          maxLength="100"
          value={formData.tagline_prefix}
          onChange={(e) => handleFieldChange('tagline_prefix', e.target.value)}
          disabled={submitting.tagline_prefix || pendingFields.tagline_prefix}
          placeholder="e.g., SCHOOL FOR"
          className="field-input"
        />
        <button
          onClick={() => handleSubmitField('tagline_prefix')}
          disabled={submitting.tagline_prefix || pendingFields.tagline_prefix || formData.tagline_prefix === originalData.tagline_prefix}
          className="submit-button"
        >
          {submitting.tagline_prefix ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Tagline Accent */}
      <div className="form-section">
        <div className="field-group">
          <label className="field-label">Tagline Accent</label>
          {pendingFields.tagline_accent && <span className="pending-badge">Pending</span>}
        </div>
        <input
          type="text"
          maxLength="150"
          value={formData.tagline_accent}
          onChange={(e) => handleFieldChange('tagline_accent', e.target.value)}
          disabled={submitting.tagline_accent || pendingFields.tagline_accent}
          placeholder="e.g., LIFE"
          className="field-input"
        />
        <button
          onClick={() => handleSubmitField('tagline_accent')}
          disabled={submitting.tagline_accent || pendingFields.tagline_accent || formData.tagline_accent === originalData.tagline_accent}
          className="submit-button"
        >
          {submitting.tagline_accent ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Philosophy */}
      <div className="form-section">
        <div className="field-group">
          <label className="field-label">Philosophy</label>
          {pendingFields.philosophy && <span className="pending-badge">Pending</span>}
        </div>
        <textarea
          maxLength="500"
          value={formData.philosophy}
          onChange={(e) => handleFieldChange('philosophy', e.target.value)}
          disabled={submitting.philosophy || pendingFields.philosophy}
          placeholder="School philosophy..."
          className="field-input textarea-field"
          rows="4"
        />
        <button
          onClick={() => handleSubmitField('philosophy')}
          disabled={submitting.philosophy || pendingFields.philosophy || formData.philosophy === originalData.philosophy}
          className="submit-button"
        >
          {submitting.philosophy ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Video File */}
      <div className="form-section">
        <div className="field-group">
          <label className="field-label">Video File</label>
          {pendingFields.video_file && <span className="pending-badge">Pending</span>}
        </div>
        
        {formData.video_file && (
          <div className="video-current">
            ✓ Current Video: <strong>{formData.video_file}</strong>
          </div>
        )}

        <div className="upload-area" style={{ marginBottom: '12px' }}>
          <input
            type="file"
            accept="video/mp4,video/webm"
            onChange={handleVideoUpload}
            disabled={videoUploading || pendingFields.video_file}
            className="file-input"
            id="video-input"
          />
          <label htmlFor="video-input" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'space-between' }}>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div className="upload-text">{videoUploading ? `Uploading ${videoProgress || 0}%...` : 'Choose Video'}</div>
              <div className="upload-hint">MP4, WebM • Max 100MB</div>
            </div>
            <div className="upload-icon">☁</div>
          </label>
        </div>

        <div className="upload-hint" style={{ marginBottom: '12px' }}>Recommended: 1920x1080px or similar widescreen ratio</div>

        <button
          onClick={() => handleSubmitField('video_file')}
          disabled={submitting.video_file || pendingFields.video_file || formData.video_file === originalData.video_file}
          className="submit-button"
        >
          {submitting.video_file ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  );
}
